// Hard validation limits (form-level: return rates as percentages, e.g. -20 means -20%)
// These MUST stay in sync with functions/src/validation.ts HARD_LIMITS_DECIMAL
export const HARD_LIMITS = {
  current_age: { min: 18, max: 100 },
  retirement_start_age: { maxYearsToRetirement: 60 },
  target_monthly_pension: { min: 1 },          // must be > 0 (min exclusive)
  current_personal_pension: { min: 0 },
  current_retirement_pension: { min: 0 },
  expected_nps_monthly: { min: 0 },
  // Form values are percentages; toPayload() divides by 100 before sending to backend
  accumulation_target_return: { min: -20, max: 30 },
  withdrawal_target_yield: { min: -20, max: 30 },
};

export const ERROR_CODES = {
  OUT_OF_RANGE: 'OUT_OF_RANGE',
  MUST_BE_POSITIVE: 'MUST_BE_POSITIVE',
  MUST_BE_NON_NEGATIVE: 'MUST_BE_NON_NEGATIVE',
  MUST_BE_GREATER_THAN_CURRENT_AGE: 'MUST_BE_GREATER_THAN_CURRENT_AGE',
  YEARS_TO_RETIREMENT_EXCEEDS_MAXIMUM: 'YEARS_TO_RETIREMENT_EXCEEDS_MAXIMUM',
};

// Validates form input (string values as entered by the user, before toPayload conversion).
// Return rates are in percentage form (e.g. "7" means 7%).
// Returns { valid: boolean, fields: Record<string, string (errorCode)> }
export function validateHardLimits(form) {
  const fields = {};
  const num = key => parseFloat(form[key]);
  const empty = key => form[key] === '' || form[key] === null || form[key] === undefined;

  const age = num('current_age');
  if (empty('current_age') || isNaN(age) || age < HARD_LIMITS.current_age.min || age > HARD_LIMITS.current_age.max) {
    fields.current_age = ERROR_CODES.OUT_OF_RANGE;
  }

  const retireAge = num('retirement_start_age');
  if (!empty('retirement_start_age') && !isNaN(retireAge) && !isNaN(age)) {
    if (retireAge <= age) {
      fields.retirement_start_age = ERROR_CODES.MUST_BE_GREATER_THAN_CURRENT_AGE;
    } else if (retireAge - age > HARD_LIMITS.retirement_start_age.maxYearsToRetirement) {
      fields.retirement_start_age = ERROR_CODES.YEARS_TO_RETIREMENT_EXCEEDS_MAXIMUM;
    }
  }

  const targetPension = num('target_monthly_pension');
  if (empty('target_monthly_pension') || isNaN(targetPension) || targetPension < HARD_LIMITS.target_monthly_pension.min) {
    fields.target_monthly_pension = ERROR_CODES.MUST_BE_POSITIVE;
  }

  for (const key of ['current_personal_pension', 'current_retirement_pension', 'expected_nps_monthly']) {
    const val = num(key);
    if (empty(key) || isNaN(val) || val < 0) {
      fields[key] = ERROR_CODES.MUST_BE_NON_NEGATIVE;
    }
  }

  const accReturn = num('accumulation_target_return');
  if (
    empty('accumulation_target_return') ||
    isNaN(accReturn) ||
    accReturn < HARD_LIMITS.accumulation_target_return.min ||
    accReturn > HARD_LIMITS.accumulation_target_return.max
  ) {
    fields.accumulation_target_return = ERROR_CODES.OUT_OF_RANGE;
  }

  const withdrawYield = num('withdrawal_target_yield');
  if (
    empty('withdrawal_target_yield') ||
    isNaN(withdrawYield) ||
    withdrawYield < HARD_LIMITS.withdrawal_target_yield.min ||
    withdrawYield > HARD_LIMITS.withdrawal_target_yield.max
  ) {
    fields.withdrawal_target_yield = ERROR_CODES.OUT_OF_RANGE;
  }

  return { valid: Object.keys(fields).length === 0, fields };
}
